/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import {
  MediaType,
  TimeHelper,
  useDispatch,
  UserStore,
  useUserSelector,
} from "@nf/common";
import { useTranslation } from "next-i18next";
import { useEffect } from "react";

import { ITableColumn, Table } from "components/Table";
import ActionsHandler from "../../ActionsHandler";

import IconX from "../../../resources/icons/close-dark.svg";

import styles from "./SettingsWatchHistory.module.scss";

interface IRowData {
  id: string | number;
  date: string;
  title: string;
}

export const SettingsWatchHistory = () => {
  const dispatch = useDispatch();
  const {
    watchHistory: { Data, Error: error, IsProcessing: isLoading },
  } = useUserSelector();
  const { t } = useTranslation("translations");

  const onDelete = (id: string | number) =>
    dispatch(UserStore.Actions.deleteWatchHistory(id));

  const watchHistory: IRowData[] | undefined = Data?.map((item) => ({
    id: item.Id,
    date: item.LastViewedTime
      ? TimeHelper.format(item.LastViewedTime, "DD-MM-YY")
      : "",
    title:
      item.MediaTypeCode === MediaType.Episode
        ? `${item.Title} - ${t("web_watch_history_season", {
            number: item.ParentOrderInParent,
          })}, ${t("web_watch_history_episode", {
            number: item.OrderInParent,
          })}`
        : item.Title ?? "",
  }));

  useEffect(() => {
    dispatch(UserStore.Actions.getWatchHistory());
  }, []);

  const columns: ITableColumn<IRowData>[] = [
    {
      key: "date",
      render: ({ date }) => date,
      align: "left",
    },
    {
      key: "title",
      render: ({ title }) => title,
      align: "left",
    },
    {
      align: "right",
      key: "action",
      className: styles.delete,
      render: ({ id, title }) => (
        <button
          type="button"
          aria-label={`${t("web_devices_icon_delete")} ${title}`}
          className={styles.deleteButton}
          onClick={() => onDelete(id)}
        >
          <IconX />
        </button>
      ),
    },
  ];

  return (
    <div className={styles.watchHistory}>
      <p className={styles.description}>{t("web_watch_history_description")}</p>
      <ActionsHandler isLoading={isLoading} error={error}>
        <Table
          rowClassName={styles.row}
          className={styles.table}
          columns={columns}
          data={watchHistory}
          rowKey="id"
          emptyText={t("web_my_account_menu_watch_history_no_data")}
        />
      </ActionsHandler>
    </div>
  );
};

export default SettingsWatchHistory;
